/**
 * An admin's "View as this user" tab (V044): the view session lives in this
 * tab's sessionStorage only. It never reads or writes the admin's own stored
 * sign-in (tokenStore.ts), so the admin's other tabs stay signed in as the
 * admin, several view tabs (different users) can be open at once, and closing
 * the tab ends it. The view token is read-only on the server and lasts at
 * most 60 minutes, with no refresh token.
 */
export const VIEW_KEY = "rai.view";
/** The page that swaps the one-time code for the view token. */
export const VIEW_AS_PATH = "/app/view-as";
/** Where a view tab goes when the view is over. */
export const VIEW_ENDED_PATH = "/app/view-ended";
/** Fired on window when the API refuses a change with 403 view_only. */
export const VIEW_ONLY_EVENT = "rai:view-only";

export interface StoredView<U = unknown> {
  token: string;
  /** epoch ms */
  expiresAt: number;
  /** the admin who is viewing, for the banner */
  viewerName: string;
  /** Research Analyst | RA client | Investor | Employee */
  label?: string;
  user: U;
}

export function readView<U = unknown>(): StoredView<U> | null {
  try {
    const raw = sessionStorage.getItem(VIEW_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as StoredView<U>;
    return typeof v?.token === "string" && typeof v.expiresAt === "number" ? v : null;
  } catch {
    return null;
  }
}

export function writeView(v: StoredView | null): void {
  try {
    if (v) sessionStorage.setItem(VIEW_KEY, JSON.stringify(v));
    else sessionStorage.removeItem(VIEW_KEY);
  } catch {
    // storage blocked: the view lasts until this page is reloaded
  }
}

/** This tab was opened to view a user (it is on the hand-over page, or holds a view). */
export function isViewLaunch(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  return path === VIEW_AS_PATH || path === VIEW_ENDED_PATH || readView() !== null;
}
